import React from "react";
import { Dealer } from "@/types/dealer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Phone, Star, Users, TrendingUp, Shield, Flag } from "lucide-react";
import { Link } from "react-router-dom";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { DealersSEO } from "@/components/dealers/DealersSEO";
import { CityStats } from "@/services/seo/cityStatsService";
import { Button } from "@/components/ui/button";

interface ServerDealersContentProps {
  dealers: Dealer[];
  nationalStats?: CityStats;
  topCities?: Array<{city: string, count: number}>;
}

/**
 * Server-side safe version of DealersContent
 * This component renders a simplified version of the dealers list during server-side rendering
 * and includes SEO optimization for server-side rendering
 */
export function ServerDealersContent({ 
  dealers, 
  nationalStats,
  topCities 
}: ServerDealersContentProps) {
  const { createDealerCanonicalPath, createCitySlug } = useDealerUtils();
  
  // If no dealers, render a loading state
  if (!dealers || dealers.length === 0) {
    return (
      <>
        {/* Include SEO component for server-side rendering */}
        <DealersSEO 
          dealers={[]} 
          nationalStats={nationalStats}
          topCities={topCities}
        />
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Compro Oro in Italia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  console.log(`[ServerDealersContent] Rendering dealers:`, dealers.length);
  
  // Get top cities for display
  const displayCities = topCities?.slice(0, 12) || [];
  
  return (
    <>
      {/* Include SEO component for server-side rendering */}
      <DealersSEO 
        dealers={dealers} 
        nationalStats={nationalStats}
        topCities={topCities}
      />
      
      <div className="space-y-8">
        {/* Hero Section with Stats */}
        <section className="text-center space-y-4">
          <h1 className="text-3xl md:text-4xl font-bold">
            Compro Oro in Italia
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Trova i migliori compro oro vicino a te. Confronta prezzi, leggi recensioni verificate 
            e ottieni quotazioni oro e argento in tempo reale.
          </p>
          
          {/* Stats Cards */}
          {nationalStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Users className="h-5 w-5 text-primary mr-2" />
                    <span className="text-2xl font-bold">{nationalStats.totalDealers}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Negozi Verificati</p>
                </CardContent>
              </Card>
              
              {nationalStats.averageRating > 0 && (
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Star className="h-5 w-5 text-yellow-500 mr-2" />
                      <span className="text-2xl font-bold">{nationalStats.averageRating.toFixed(1)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Rating Medio</p>
                  </CardContent>
                </Card>
              )}
              
              {nationalStats.verifiedDealers > 0 && (
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Shield className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-2xl font-bold">{nationalStats.verifiedDealers}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Negozi Certificati</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </section>

        {/* Top Cities Section */}
        {displayCities.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-6">Compro Oro per Città</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {displayCities.map((cityData) => (
                <Link
                  key={cityData.city}
                  to={`/compro-oro/${createCitySlug(cityData.city)}`}
                  className="block"
                >
                  <Card className="h-full hover:shadow-md transition-shadow">
                    <CardContent className="p-4 text-center">
                      <h3 className="font-semibold mb-1">{cityData.city}</h3>
                      <p className="text-sm text-muted-foreground">
                        {cityData.count} {cityData.count === 1 ? 'negozio' : 'negozi'}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Featured Dealers Section */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Compro Oro in Evidenza</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dealers.slice(0, 9).map((dealer) => (
              <Link
                key={dealer.id}
                to={createDealerCanonicalPath(dealer.id, dealer.city)}
                className="block"
              >
                <Card className="h-full hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-2">{dealer.name}</h3>
                    
                    {/* Address */}
                    <div className="flex items-start mb-2">
                      <MapPin className="h-4 w-4 mr-2 text-muted-foreground shrink-0 mt-1" />
                      <div>
                        <p className="text-sm">{dealer.address}</p>
                        {dealer.city && (
                          <p className="text-sm">{dealer.city}{dealer.postal_code ? `, ${dealer.postal_code}` : ''}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Phone */}
                    {dealer.phone && (
                      <div className="flex items-center mb-2">
                        <Phone className="h-4 w-4 mr-2 text-muted-foreground shrink-0" />
                        <p className="text-sm">{dealer.phone}</p>
                      </div>
                    )}
                    
                    {/* Rating */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 mr-1 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm font-medium">
                          {typeof dealer.rating === 'number' ? dealer.rating.toFixed(1) : '4.0'}
                        </span>
                        <span className="text-sm text-muted-foreground ml-1">/ 5</span>
                      </div>
                      
                      {/* Report Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Navigate to dealer detail page with report modal
                          window.location.href = createDealerCanonicalPath(dealer.id, dealer.city) + '?report=true';
                        }}
                        className="h-8 px-2 text-muted-foreground hover:text-destructive"
                        title="Segnala questo commerciante"
                      >
                        <Flag className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          {dealers.length > 9 && (
            <div className="text-center mt-6">
              <p className="text-muted-foreground">
                E altri {dealers.length - 9} negozi in tutta Italia
              </p>
            </div>
          )}
        </section>

        {/* Benefits Section */}
        <section className="bg-muted/50 rounded-lg p-6">
          <h2 className="text-2xl font-bold mb-6 text-center">Perché Scegliere Gildy</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Quotazioni in Tempo Reale</h3>
              <p className="text-sm text-muted-foreground">
                Prezzi aggiornati costantemente basati sulle quotazioni internazionali dell'oro
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Negozi Verificati</h3>
              <p className="text-sm text-muted-foreground">
                Tutti i compro oro sono controllati e certificati per garantire trasparenza
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Recensioni Reali</h3>
              <p className="text-sm text-muted-foreground">
                Leggi le esperienze di altri clienti per scegliere il negozio migliore
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
